/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Button,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import PropTypes from 'prop-types';
import LISTING_COLUMNS from '../../config/module-configs/content-channel-rulesets.config';
import Iconify from '../common/iconify.component';
import {
  getChannelRulesetList,
  incrementalRunAssignment,
  incrementalRunHeroSchedule,
  incrementalRunSchedule,
} from '../../services/apps.service';
import CellValue from '../table-elements/cell-value.component';
import NoData from '../table-elements/no-data.component';
import Error from '../table-elements/error.component';
import Loading from '../table-elements/loading.component';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import useAccess from '../../hooks/access.hook';

const ListChannelRulesets = ({ processing, setProcessing }) => {
  const columns = LISTING_COLUMNS;
  const { hasOperationAccess } = useAccess();

  const [options, setOptions] = useState({
    rows: [],
    reloadCounter: 0,
    error: false,
    loading: true,
  });
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [dataProcessingCnt, setDataProcessingCnt] = useState(0);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const handleRulesetIncrementalRun = async () => {
    try {
      setProcessing(true);
      setShowConfirmationDialog(false);

      for (let i = 0; i < options.rows.length; i += 1) {
        setDataProcessingCnt((prev) => prev + 1);

        const { id } = options.rows[i];
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((item) =>
            item.id === id ? { ...item, showAssignmentLoader: true } : item
          ),
        }));

        try {
          await incrementalRunAssignment(id);
        } catch (er) {
          // Do nothing and continue with next
        } finally {
          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === id ? { ...item, showAssignmentLoader: false } : item
            ),
          }));
        }
      }

      for (let i = 0; i < options.rows.length; i += 1) {
        setDataProcessingCnt((prev) => prev + 1);

        const { id } = options.rows[i];
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((item) =>
            item.id === id ? { ...item, showSchedulingLoader: true } : item
          ),
        }));

        try {
          await incrementalRunSchedule(id);
        } catch (er) {
          // Do nothing and continue with next
        } finally {
          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === id ? { ...item, showSchedulingLoader: false } : item
            ),
          }));
        }
      }

      for (let i = 0; i < options.rows.length; i += 1) {
        setDataProcessingCnt((prev) => prev + 1);

        const { id } = options.rows[i];
        setOptions((prev) => ({
          ...prev,
          rows: prev.rows.map((item) =>
            item.id === id ? { ...item, showHeroSchedulingLoader: true } : item
          ),
        }));

        try {
          await incrementalRunHeroSchedule(id);
        } catch (er) {
          // Do nothing and continue with next
        } finally {
          setOptions((prev) => ({
            ...prev,
            rows: prev.rows.map((item) =>
              item.id === id
                ? { ...item, showHeroSchedulingLoader: false }
                : item
            ),
          }));
        }
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Something went wrong, please try again.';

      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'error',
        message,
      });
    }

    setProcessing(false);
  };

  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    getChannelRulesetList()
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          rows: res?.data || [],
          error: false,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <>
      <Box
        sx={{
          transition: 'margin 0.3s ease, width 0.3s ease',
          width: '100%',
        }}
      >
        <Stack direction="row" justifyContent="end" sx={{ px: 0, py: 2 }}>
          <div>
            {hasOperationAccess('rule_based_scheduling', 'manage') && (
              <Button
                color={processing ? 'secondary' : 'primary'}
                variant="contained"
                startIcon={
                  processing ? <Iconify icon="line-md:loading-loop" /> : ''
                }
                onClick={() => setShowConfirmationDialog(true)}
                disabled={options.loading || processing}
                sx={{ mr: 1 }}
              >
                {processing
                  ? `Processing ${dataProcessingCnt} of ${
                      options.rows.length * 3
                    }`
                  : 'Incremental Run Ruleset for All Channels'}
              </Button>
            )}

            <Button
              color="inherit"
              variant="contained"
              startIcon={<Iconify icon="ic:twotone-refresh" />}
              onClick={handleRefreshData}
              disabled={options.loading || processing}
            >
              Refresh
            </Button>
          </div>
        </Stack>

        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={`col-${c.id}`}
                      align={c.align}
                      width={c.width}
                      sx={{
                        cursor: c.sortable ? 'pointer' : 'inherit',
                        padding: '16px',
                        fontSize: '0.875rem !important',
                      }}
                    >
                      <Stack direction="row" justifyContent={c.align}>
                        {c.label}
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {!options.loading && !options.error && (
                  <>
                    {options.rows.map((r, index) => (
                      <TableRow key={`row-${r[columns[0].dataKey]}-${index}`}>
                        {columns.map((c) => {
                          const showLoader =
                            (c.dataKey === 'assignmentRules' &&
                              r.showAssignmentLoader === true) ||
                            (c.dataKey === 'schedulingRules' &&
                              r.showSchedulingLoader === true) ||
                            (c.dataKey === 'heroPlaylistSchedulingRules' &&
                              r.showHeroSchedulingLoader === true);

                          const showTick =
                            (c.dataKey === 'assignmentRules' &&
                              r.showAssignmentLoader === false) ||
                            (c.dataKey === 'schedulingRules' &&
                              r.showSchedulingLoader === false) ||
                            (c.dataKey === 'heroPlaylistSchedulingRules' &&
                              r.showHeroSchedulingLoader === false);

                          return (
                            <TableCell
                              key={`data-col-${c.id}-${r[c.dataKey]}`}
                              align={c.align}
                              width={c.width}
                              sx={{
                                padding: '16px',
                                fontSize: '0.875rem !important',
                              }}
                              variant="string"
                            >
                              {showLoader ? (
                                <Iconify icon="line-md:loading-loop" />
                              ) : showTick ? (
                                <Iconify icon="charm:tick" />
                              ) : (
                                <CellValue
                                  type={c.type}
                                  dataKey={c.dataKey}
                                  value={r[c.dataKey]}
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </>
                )}

                {!options.loading &&
                  !options.error &&
                  options.totalRows === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={columns.length}>
                        <NoData />
                      </TableCell>
                    </TableRow>
                  )}

                {!options.loading &&
                  options.error &&
                  options.totalRows === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={columns.length}>
                        <Error />
                      </TableCell>
                    </TableRow>
                  )}

                {options.loading && (
                  <TableRow>
                    <TableCell align="center" colSpan={columns.length}>
                      <Loading />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {showConfirmationDialog && (
        <ConfirmPopup
          title="Incremental Run Ruleset for All Channels"
          message="Do you want to run rulesets for all channels to for assignment and scheduling for all the channels?"
          onCancel={() => setShowConfirmationDialog(false)}
          onSuccess={() => handleRulesetIncrementalRun()}
          successButtonText="Proceed"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="confirm"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ListChannelRulesets.propTypes = {
  processing: PropTypes.bool.isRequired,
  setProcessing: PropTypes.func.isRequired,
};

export default ListChannelRulesets;
